import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LanguageProvider } from './context/LanguageContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

// Landing loaded eagerly (first page users see)
import Landing from './pages/Landing';

// After a deploy, any tab that was open before still has the previous main.js
// cached. When React.lazy tries to fetch a chunk with a hash that no longer
// exists on the server, it throws ChunkLoadError. Wrap each lazy import so the
// first time this happens we hard-reload the page — the browser re-fetches
// index.html + the new main.js, and the user continues without seeing a white
// screen. `sessionStorage` guard prevents an infinite reload loop if the failure
// is a genuine network/server problem.
const lazyWithReload = (factory) =>
  lazy(() =>
    factory().catch((err) => {
      const isChunkError =
        err && (err.name === 'ChunkLoadError' ||
                /Loading (CSS )?chunk [\d]+ failed/i.test(err.message || ''));
      if (isChunkError && !sessionStorage.getItem('mesamind_chunk_reloaded')) {
        sessionStorage.setItem('mesamind_chunk_reloaded', '1');
        window.location.reload();
        // Return a pending promise — reload will tear down this component before it resolves.
        return new Promise(() => {});
      }
      throw err;
    })
  );

// All other pages lazy-loaded for faster initial load (MSM-012 fix)
const StudentPortal = lazyWithReload(() => import('./pages/StudentPortal'));
const TeacherPortal = lazyWithReload(() => import('./pages/TeacherPortal'));
const AdminPortal = lazyWithReload(() => import('./pages/AdminPortal'));
const DrawingCanvas = lazyWithReload(() => import('./pages/DrawingCanvas'));
const SchoolPortal = lazyWithReload(() => import('./pages/SchoolPortal'));
const StudentReport = lazyWithReload(() => import('./pages/StudentReport'));
const ClassReport = lazyWithReload(() => import('./pages/ClassReport'));
const SchoolReport = lazyWithReload(() => import('./pages/SchoolReport'));

// 404 fallback for unmatched routes
const NotFound = () => (
  <div style={{
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', height: '100vh', background: '#f8fafc',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  }}>
    <div style={{ fontSize: 72, fontWeight: 700, color: '#6366f1' }}>404</div>
    <div style={{ fontSize: 18, color: '#64748b', marginBottom: 24 }}>Page not found</div>
    <a href="/" style={{
      padding: '10px 24px', background: '#6366f1', color: '#fff',
      borderRadius: 8, textDecoration: 'none', fontWeight: 500,
    }}>Go Home</a>
  </div>
);

// Loading fallback shown while lazy chunks load
const PageLoader = () => (
  <div style={{
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    height: '100vh', background: '#f8fafc',
  }}>
    <div style={{
      width: 40, height: 40, border: '3px solid #e2e8f0',
      borderTopColor: '#6366f1', borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// RoleGuard — redirects to / if user is not authenticated or lacks required role
// PF-001/PF-002 fix: prevents cross-role portal access
const RoleGuard = ({ roles, children }) => {
  const { user, loading, isAuthenticated } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/" replace />;
  if (roles && !roles.includes(user?.role)) return <Navigate to="/" replace />;
  return children;
};

// Create query client for data fetching
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <LanguageProvider>
          <Router>
            <div className="App">
              <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Navigate to="/" replace />} />

                {/* Role-guarded portals — PF-001/PF-002 fix */}
                <Route path="/student" element={
                  <RoleGuard roles={['student']}><ErrorBoundary><StudentPortal /></ErrorBoundary></RoleGuard>
                } />
                <Route path="/teacher" element={
                  <RoleGuard roles={['teacher']}><ErrorBoundary><TeacherPortal /></ErrorBoundary></RoleGuard>
                } />
                <Route path="/admin" element={
                  <RoleGuard roles={['admin']}><ErrorBoundary><AdminPortal /></ErrorBoundary></RoleGuard>
                } />
                <Route path="/school" element={
                  <RoleGuard roles={['school', 'admin']}><ErrorBoundary><SchoolPortal /></ErrorBoundary></RoleGuard>
                } />

                {/* Drawing canvas — accessible to anyone (demo mode for unauthenticated) */}
                <Route path="/canvas" element={
                  <DrawingCanvas />
                } />
                <Route path="/canvas/:artworkId" element={
                  <RoleGuard><DrawingCanvas /></RoleGuard>
                } />

                {/* Report routes — teachers and admins */}
                <Route path="/reports/student" element={
                  <RoleGuard roles={['teacher', 'admin', 'school']}><StudentReport /></RoleGuard>
                } />
                <Route path="/reports/student/:studentId" element={
                  <RoleGuard roles={['teacher', 'admin', 'school']}><StudentReport /></RoleGuard>
                } />
                <Route path="/reports/class/:classId" element={
                  <RoleGuard roles={['teacher', 'admin', 'school']}><ClassReport /></RoleGuard>
                } />
                <Route path="/reports/school" element={
                  <RoleGuard roles={['admin', 'school']}><SchoolReport /></RoleGuard>
                } />
                <Route path="/reports/school/:schoolId" element={
                  <RoleGuard roles={['admin', 'school']}><SchoolReport /></RoleGuard>
                } />
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
            </div>
          </Router>
        </LanguageProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
